/**
 * InstantiatePlaybookModal (DEV-1278 → DEV-1282 Step 1) — create a new
 * mini-project FROM a published playbook. The playbook supplies the
 * structure (goals/mission, tasks, BOM, roles, knowledge baselines); the
 * instantiator confirms where it runs and the run-specific details.
 *
 * Step 1 (Setup): hero banner · organization running the project · name ·
 * start/target-end dates · estimated cost. Metadata schema is inherited
 * from the playbook master server-side. (Primary location picker, goal-
 * target confirmation, and team assembly are the next steps — DEV-1282.)
 *
 * "Organization running project" = orgs where the user is an
 * admin/content_creator (owner/admin/content_creator role), plus the
 * user's workshop. A PRIVATE playbook may only run in its creating org.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  instantiateMiniProjectFromPlaybook,
  transitionMiniProject,
  dispatchMiniProjectMutated,
  type MiniProjectPlaybook,
} from "../../api/miniProjects";
import { useApiSecurityData } from "../../api/useApiSecurityData";
import { mediaSrc } from "../../api/mediaUrl";
import { fetchMiniProjectRoles, inviteToMiniProjectRole } from "../../api/miniProjectRoles";
import { eligibleRunOrgs } from "../../Data/MiniProjectConfig/playbookEligibility";
import GeoLocationPicker from "../GeoLocationPicker/GeoLocationPicker";
import styles from "./InstantiatePlaybookModal.module.css";

/** Same shortlist the create / settings budget editors use. */
const COMMON_CURRENCIES = [
  "USD", "EUR", "GBP", "CAD", "AUD", "JPY", "CHF", "CNY",
  "INR", "MXN", "BRL", "ZAR", "SEK", "NOK", "DKK",
];

interface Props {
  playbook: MiniProjectPlaybook;
  currentUserGuid: string;
  communities: Array<Record<string, any>>;
  relationships?: Array<Record<string, any>>;
  onClose: () => void;
  onCreated: (miniProjectGuid: string) => void;
}

export default function InstantiatePlaybookModal({
  playbook,
  currentUserGuid,
  communities,
  relationships = [],
  onClose,
  onCreated,
}: Props) {
  const { t } = useAppTranslation("pages");
  const { communityRoles, communityMemberships, securityUsers } = useApiSecurityData();

  const isSysadmin = useMemo(
    () =>
      (securityUsers || []).some(
        (u: any) =>
          String(u.user_guid || "").toUpperCase() === currentUserGuid.toUpperCase() &&
          !!u.is_system_admin,
      ),
    [securityUsers, currentUserGuid],
  );

  // Eligible orgs to RUN this playbook in (shared with the catalog's
  // show/hide decision).
  const options = useMemo(
    () => eligibleRunOrgs(playbook, currentUserGuid, communities, relationships, communityRoles || [], isSysadmin),
    [playbook, currentUserGuid, communities, relationships, communityRoles, isSysadmin],
  );

  const [title, setTitle] = useState(playbook.title || "");
  const [communityGuid, setCommunityGuid] = useState(options.length === 1 ? options[0].guid : "");
  const [locationGuid, setLocationGuid] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [budgetAmount, setBudgetAmount] = useState(
    playbook.plannedBudgetAmount != null ? String(playbook.plannedBudgetAmount) : "",
  );
  const [budgetCurrency, setBudgetCurrency] = useState(playbook.plannedBudgetCurrency || "USD");
  const [enrollInCollection, setEnrollInCollection] = useState(true);
  const [visibility, setVisibility] = useState<"team_only" | "community" | "public">("community");
  const [activateNow, setActivateNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-suggest the target end date from the playbook's typical duration
  // when a start date is chosen and no end date has been set yet.
  useEffect(() => {
    if (startDate && !endDate && playbook.typicalDurationDays && playbook.typicalDurationDays > 0) {
      const d = new Date(startDate + "T00:00:00");
      d.setDate(d.getDate() + playbook.typicalDurationDays);
      setEndDate(d.toISOString().slice(0, 10));
    }
    // Intentional: fire only on start-date change — don't re-suggest once the
    // user has touched the end date, and don't loop on endDate updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate]);

  const [step, setStep] = useState(0);
  const [goalTargets, setGoalTargets] = useState<Record<string, string>>({});

  // Lock the body to Step 1's natural height so the modal never resizes
  // between steps AND Step 1 never scrolls; taller steps (Goals/Team) scroll
  // within that height. Measured once on the first (Setup) render.
  const bodyRef = useRef<HTMLDivElement>(null);
  const [lockedBodyH, setLockedBodyH] = useState<number | null>(null);
  // While the user is on Step 1, track its NATURAL height (Step 1 is left
  // unclamped) via a ResizeObserver, so async-expanded fields like the org
  // list are captured. The later steps then clamp to this exact height, so the
  // dialog never changes size between steps.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el || step !== 0) return;
    const measure = () =>
      setLockedBodyH((prev) => {
        const h = el.scrollHeight;
        return h > 0 && h !== prev ? h : prev;
      });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [step]);

  // Step 2 goals — parsed from the playbook's published mission KPIs.
  const goals = useMemo(() => {
    if (!playbook.publishedContent) return [] as { key: string; name: string; target: number | null; uom: string | null }[];
    try {
      const parsed = JSON.parse(playbook.publishedContent);
      const mk = Array.isArray(parsed?.missionKpis) ? parsed.missionKpis : [];
      return mk
        .map((g: any) => ({
          key: String(g?.key ?? ""),
          name: String(g?.displayName ?? "").trim() || "Goal",
          target: g?.targetGoal ?? null,
          uom: g?.uom ?? null,
        }))
        .filter((g: any) => g.key);
    } catch {
      return [];
    }
  }, [playbook.publishedContent]);
  const hasGoalsStep = goals.length > 0;

  // Step 3 — playbook's seeded roles (parsed from published_content.roles).
  const playbookRoles = useMemo(() => {
    if (!playbook.publishedContent) return [] as { key: string; name: string }[];
    try {
      const parsed = JSON.parse(playbook.publishedContent);
      const rs = Array.isArray(parsed?.roles) ? parsed.roles : [];
      return rs
        .map((r: any) => ({ key: String(r?.key ?? ""), name: String(r?.name ?? "").trim() || "Role" }))
        .filter((r: any) => r.key);
    } catch {
      return [];
    }
  }, [playbook.publishedContent]);
  const hasTeamStep = playbookRoles.length > 0;
  const [teamAssignments, setTeamAssignments] = useState<Record<string, string>>({});

  // Members of the chosen org, to invite into the seeded roles.
  const members = useMemo(() => {
    const cg = communityGuid.toUpperCase();
    if (!cg) return [] as { guid: string; name: string }[];
    const ids = new Set<string>();
    for (const m of communityMemberships || []) {
      if (String(m.community_entity_guid || "").toUpperCase() === cg && (m.is_active ?? true) !== false) {
        ids.add(String(m.user_guid || "").toUpperCase());
      }
    }
    for (const r of communityRoles || []) {
      if (String(r.community_entity_guid || "").toUpperCase() === cg && (r.is_active ?? true) !== false) {
        ids.add(String(r.user_guid || "").toUpperCase());
      }
    }
    const byGuid = new Map(
      (securityUsers || []).map((u: any) => [String(u.user_guid || "").toUpperCase(), u]),
    );
    return [...ids]
      .map((id) => ({ guid: id, name: String(byGuid.get(id)?.display_name || "").trim() }))
      .filter((x) => x.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [communityGuid, communityMemberships, communityRoles, securityUsers]);

  // Steps present in this wizard, in order.
  const stepKeys = useMemo(
    () => ["setup", ...(hasGoalsStep ? ["goals"] : []), ...(hasTeamStep ? ["team"] : [])],
    [hasGoalsStep, hasTeamStep],
  );
  const curKey = stepKeys[step] ?? "setup";
  const isLastStep = step >= stepKeys.length - 1;

  const hasCollection = !!playbook.collectionGuid;
  const valid = useMemo(
    () => title.trim().length > 0 && communityGuid.length > 0,
    [title, communityGuid],
  );

  const handleCreate = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const hasBudget = budgetAmount.trim().length > 0;
      const created = await instantiateMiniProjectFromPlaybook({
        playbookGuid: playbook.playbookGuid,
        title: title.trim(),
        ownerCommunityGuid: communityGuid,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        visibility,
        locationGeographyGuid: locationGuid || undefined,
        optOutOfCollection: hasCollection ? !enrollInCollection : undefined,
        plannedBudgetAmount: hasBudget ? Number(budgetAmount) : null,
        plannedBudgetCurrency: hasBudget ? budgetCurrency : null,
        goalTargetOverrides: goals.map((g) => {
          const raw = goalTargets[g.key] ?? (g.target != null ? String(g.target) : "");
          return { key: g.key, targetGoal: raw.trim() ? Number(raw) : null };
        }),
      });
      const guid = (created as any).miniProjectGuid ?? (created as any).mini_project_guid;

      // Team (DEV-1285): invite the chosen members into the seeded roles.
      // Best-effort — reuses the tested role-invitation endpoint; the project
      // stands regardless of any individual invite outcome.
      const assignments = Object.entries(teamAssignments).filter(([, u]) => u);
      if (guid && assignments.length > 0) {
        try {
          const instanceRoles = await fetchMiniProjectRoles(guid);
          const roleByKey = new Map(
            instanceRoles
              .filter((r) => r.playbookRoleKey)
              .map((r) => [r.playbookRoleKey as string, r.roleGuid]),
          );
          await Promise.allSettled(
            assignments.map(([roleKey, userGuid]) => {
              const rg = roleByKey.get(roleKey);
              return rg ? inviteToMiniProjectRole(guid, rg, { userGuid }) : Promise.resolve();
            }),
          );
        } catch {
          // invitations are best-effort; the created project stands.
        }
      }

      // Activate Now (DEV-1285): transition the fresh draft straight to active.
      if (activateNow && guid) {
        await transitionMiniProject(guid, { newStatus: "active" });
      }
      onCreated(guid);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }, [playbook.playbookGuid, title, communityGuid, startDate, endDate, locationGuid, budgetAmount, budgetCurrency, visibility, hasCollection, enrollInCollection, goals, goalTargets, teamAssignments, activateNow, onCreated]);

  const dialog = (
    <div className={styles.backdrop} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={styles.hero}>
          {playbook.heroImageUrl ? (
            <img className={styles.heroImg} src={mediaSrc(playbook.heroImageUrl) ?? undefined} alt="" />
          ) : (
            <span className={styles.heroFallback} aria-hidden="true">▦</span>
          )}
          <button className={styles.xOnHero} onClick={onClose} aria-label={t("miniProject.completion.close")}>✕</button>
        </div>

        <div
          className={styles.body}
          ref={bodyRef}
          style={
            // Step 1 stays natural (its height is the reference). Later steps
            // clamp to that exact height — `flex: none` so it wins over the
            // CSS `flex: 1`, which would otherwise re-size the body per step.
            step > 0 && lockedBodyH
              ? {
                  height: lockedBodyH,
                  minHeight: lockedBodyH,
                  maxHeight: lockedBodyH,
                  flex: "none",
                }
              : undefined
          }
        >
          <div className={styles.fromLabel}>{t("instantiate.fromLabel")}</div>
          <h2 className={styles.title}>{t("instantiate.title")}</h2>
          {playbook.primaryOutcome && (
            <div className={styles.outcome}>
              <span className={styles.outcomeIcon} aria-hidden="true">◎</span>
              {playbook.primaryOutcome}
            </div>
          )}

          {stepKeys.length > 1 && (
            <div className={styles.steps}>
              {stepKeys.map((k, i) => (
                <span
                  key={k}
                  className={i === step ? styles.stepNow : i < step ? styles.stepDone : styles.stepPend}
                >
                  {i + 1} · {t(`instantiate.step${k.charAt(0).toUpperCase()}${k.slice(1)}`)}
                </span>
              ))}
            </div>
          )}

          {curKey === "setup" && (
          <>
          <label className={styles.fl}>{t("instantiate.nameLabel")}</label>
          <input
            className={styles.input}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("instantiate.namePlaceholder")}
            maxLength={200}
            autoFocus
          />

          <label className={styles.fl}>{t("instantiate.orgLabel")}</label>
          {options.length === 0 ? (
            <div className={styles.hint}>{t("instantiate.orgNone")}</div>
          ) : (
            <select className={styles.input} value={communityGuid} onChange={(e) => setCommunityGuid(e.target.value)}>
              <option value="">{t("instantiate.orgPick")}</option>
              {options.map((c) => (
                <option key={c.guid} value={c.guid}>{c.label}</option>
              ))}
            </select>
          )}

          <label className={styles.fl}>{t("instantiate.locationLabel")}</label>
          <GeoLocationPicker
            value={locationGuid}
            onChange={setLocationGuid}
            entities={Array.isArray(communities) ? communities : []}
            relationships={Array.isArray(relationships) ? relationships : []}
            placeholder={t("instantiate.locationPlaceholder")}
          />

          <div className={styles.dates}>
            <div className={styles.dateCol}>
              <label className={styles.fl}>{t("instantiate.startLabel")}</label>
              <input className={styles.input} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className={styles.dateCol}>
              <label className={styles.fl}>{t("instantiate.endLabel")}</label>
              <input className={styles.input} type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
          </div>

          <label className={styles.fl}>{t("instantiate.costLabel")}</label>
          <div className={styles.budgetRow}>
            <input
              className={styles.input}
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={budgetAmount}
              onChange={(e) => setBudgetAmount(e.target.value)}
              placeholder="0.00"
            />
            <select className={styles.currency} value={budgetCurrency} onChange={(e) => setBudgetCurrency(e.target.value)} aria-label={t("instantiate.costLabel")}>
              {COMMON_CURRENCIES.map((c) => (<option key={c} value={c}>{c}</option>))}
            </select>
          </div>

          <label className={styles.fl}>{t("instantiate.visibilityLabel")}</label>
          <select
            className={styles.input}
            value={visibility}
            onChange={(e) => setVisibility(e.target.value as "team_only" | "community" | "public")}
          >
            <option value="team_only">{t("instantiate.visTeamOnly")}</option>
            <option value="community">{t("instantiate.visCommunity")}</option>
            <option value="public">{t("instantiate.visPublic")}</option>
          </select>
          <div className={styles.hint}>{t("instantiate.visibilityHint")}</div>

          {hasCollection && (
            <label className={styles.checkRow}>
              <input type="checkbox" checked={enrollInCollection} onChange={(e) => setEnrollInCollection(e.target.checked)} />
              <span>{t("instantiate.collectionEnroll")}</span>
            </label>
          )}

          <div className={styles.note}>
            <span aria-hidden="true">ℹ</span> {t("instantiate.draftHint")}
          </div>
          </>
          )}

          {curKey === "goals" && (
            <div className={styles.goalsStep}>
              <div className={styles.stepIntro}>{t("instantiate.goalsIntro")}</div>
              {goals.map((g) => (
                <div key={g.key} className={styles.goalRow}>
                  <span className={styles.goalName}>{g.name}</span>
                  <input
                    className={styles.goalInput}
                    type="number"
                    min="0"
                    step="any"
                    inputMode="decimal"
                    value={goalTargets[g.key] ?? (g.target != null ? String(g.target) : "")}
                    onChange={(e) => setGoalTargets((prev) => ({ ...prev, [g.key]: e.target.value }))}
                  />
                  {g.uom && <span className={styles.goalUom}>{g.uom}</span>}
                </div>
              ))}
            </div>
          )}

          {curKey === "team" && (
            <div className={styles.goalsStep}>
              <div className={styles.stepIntro}>{t("instantiate.teamIntro")}</div>
              {members.length === 0 && <div className={styles.hint}>{t("instantiate.teamNoMembers")}</div>}
              {playbookRoles.map((r) => (
                <div key={r.key} className={styles.teamRow}>
                  <span className={styles.teamRole}>{r.name}</span>
                  <select
                    className={styles.teamSelect}
                    value={teamAssignments[r.key] || ""}
                    onChange={(e) => setTeamAssignments((prev) => ({ ...prev, [r.key]: e.target.value }))}
                    disabled={members.length === 0}
                  >
                    <option value="">{t("instantiate.teamUnassigned")}</option>
                    {members.map((m) => (
                      <option key={m.guid} value={m.guid}>{m.name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}

          {isLastStep && (
            <label className={styles.activateRow}>
              <input type="checkbox" checked={activateNow} onChange={(e) => setActivateNow(e.target.checked)} />
              <span>{t("instantiate.activateNow")}</span>
            </label>
          )}

          {error && <div className={styles.error}>{error}</div>}
        </div>

        <div className={styles.foot}>
          {step > 0 && (
            <button className={styles.btnGhost} onClick={() => setStep((s) => s - 1)} disabled={busy}>
              {t("instantiate.back")}
            </button>
          )}
          <span className={styles.spacer} />
          <button className={styles.btnGhost} onClick={onClose} disabled={busy}>
            {t("miniProject.completion.cancel")}
          </button>
          {!isLastStep ? (
            <button
              className={styles.btnPrimary}
              onClick={() => setStep((s) => s + 1)}
              disabled={curKey === "setup" && !valid}
            >
              {t("instantiate.next")}
            </button>
          ) : (
            <button className={styles.btnPrimary} onClick={handleCreate} disabled={busy || !valid}>
              {busy ? t("instantiate.creating") : t("instantiate.create")}
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(dialog, document.body);
}
